import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Clock, Calendar, ArrowRight, User } from 'lucide-react';
import { blogPostsData } from '../data/blogPostsData';
import InquiryModal from '../components/InquiryModal';

const Blogs = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buttonContext, setButtonContext] = useState('');
  const handleButtonClick = (context) => { setButtonContext(context); setIsModalOpen(true); };

  return (
    <div className="min-h-screen pt-16 bg-white">
      {/* Hero */}
      <section className="relative bg-[#1A3628] py-24 overflow-hidden" data-testid="blogs-hero">
               <div className="absolute inset-0  bg-center" style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1695388474402-ed805a890d8d?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85")' }}><div className="absolute inset-0 bg-gradient-to-br from-[#1A3628]/90 via-[#11241A]/90 to-[#0D1910]/95"></div></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center relative z-10">
          <p className="eyebrow text-[#C85A40] mb-6">Expert Insights</p>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-white mb-8 tracking-tighter leading-tight">
            IP Strategies That <span className="text-[#C85A40]">Drive Real Results</span>
          </h1>
          <p className="font-body text-base text-white/70 max-w-3xl mx-auto leading-relaxed">
            Proven strategies, real case studies, and actionable insights from protecting 10,000+ brands. Learn how successful enterprises leverage IP for competitive advantage.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-24 bg-[#EAE7DF]" data-testid="blogs-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12">
            <p className="eyebrow text-[#C85A40] mb-2">Knowledge Base</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-[#111C16] tracking-tighter">Latest Articles</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
            {blogPostsData.map((post) => (
              <a href={`/blogs/${post.id}`} key={post.id} className="block group" data-testid={`blog-card-${post.id}`}>
                <div className="bg-white border border-[#D5D1C8] hover-lift hover-gold-border h-full flex flex-col overflow-hidden">
                  {/* Image */}
                  <div className="relative h-52 overflow-hidden hover-zoom">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <span className="inline-block px-3 py-1 bg-[#1A3628] text-white text-xs font-body font-semibold uppercase tracking-wider">{post.category}</span>
                    </div>
                    {post.featured && (
                      <div className="absolute top-4 right-4">
                        <span className="inline-block px-3 py-1 bg-[#C85A40] text-[#111C16] text-xs font-body font-semibold uppercase tracking-wider">Featured</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="font-display text-lg font-bold text-[#111C16] mb-3 leading-tight min-h-[56px] group-hover:text-[#1A3628] transition-colors">
                      {post.title}
                    </h3>
                    <p className="font-body text-sm text-[#4A5D53] mb-4 leading-relaxed line-clamp-3 flex-grow">{post.excerpt}</p>
                    
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.slice(0, 2).map((tag, idx) => (
                        <span key={idx} className="font-body text-xs font-medium px-2 py-0.5 bg-[#C85A40]/10 text-[#C85A40]">{tag}</span>
                      ))}
                    </div>
                    
                    {/* Author */}
                    <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[#D5D1C8]">
                      <div className="w-8 h-8 bg-[#1A3628] flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-[#C85A40]" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="font-body text-xs font-semibold text-[#111C16]">{post.author}</p>
                        <p className="font-body text-xs text-[#4A5D53/60]">{post.role}</p>
                      </div>
                    </div>
                    
                    {/* Meta */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center font-body text-xs text-[#4A5D53/60]">
                          <Clock className="h-3.5 w-3.5 mr-1 text-[#C85A40]" strokeWidth={1.5} />{post.readTime}
                        </div>
                        <div className="flex items-center font-body text-xs text-[#4A5D53/60]">
                          <Calendar className="h-3.5 w-3.5 mr-1 text-[#C85A40]" strokeWidth={1.5} />{post.date}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-[#C85A40] font-body text-xs font-semibold group-hover:gap-2 transition-all">
                        Read <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      {/* <section className="relative py-16 md:py-20 bg-[#1A3628] overflow-hidden" data-testid="blogs-cta">
        <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(ellipse at 50% 50%, #2D5A43 0%, transparent 70%)' }} />
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center relative z-10">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tighter mb-6">Ready to Protect Your Brand?</h2>
          <p className="font-body text-lg text-white/50 mb-12 max-w-xl mx-auto leading-relaxed">
            Join 10,000+ enterprises that have secured their competitive advantage with our expert IP protection services.
          </p>
          <Button onClick={() => handleButtonClick('Start Protection - Blogs CTA')} className="bg-[#C85A40] hover:bg-[#A64730] text-white btn-hover" data-testid="blogs-cta-start-btn">Start Your Protection</Button>
        </div>
      </section> */}

      <InquiryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} buttonContext={buttonContext} />
    </div>
  );
};

export default Blogs;
